import { DialogCloseError, DialogCloseResult } from 'aurelia-dialog';
import { autoinject, newInstance, PLATFORM } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { ValidationController, ValidationRules } from 'aurelia-validation';

import {
	BaseDialog,
	BaseDialogModel,
	BaseDialogModelDefaults,
} from '~/components/dialog/base-dialog';
import { Notification } from '~/components/notification/notification';
import { JQueryHelper } from '~/utils/jquery-helper';
import { ValidationMessageRenderer } from '~/validation/validation-message-renderer';

export interface ExcelExportDialogModel extends BaseDialogModel {
	numberToExport?: number;
	recordsTotal?: number;
	configLimit?: number;
}

export const ExcelExportDialogModelDefaults: ExcelExportDialogModel = {
	...BaseDialogModelDefaults,
	dialogSize: BaseDialog.DialogSize.EXTRA_SMALL,
	headingText: 'export-modal.export-to-excel',
};

@autoinject
export class ExcelExportDialog extends BaseDialog<ExcelExportDialogModel> {
	private readonly EXPORT_RANGE = 'EXPORT_RANGE';
	private totalExceedsConfig: boolean = false;

	constructor(
		@newInstance() readonly validationController: ValidationController,
		@newInstance()
		readonly validationMessageRenderer: ValidationMessageRenderer,
		private i18n: I18N,
		...rest
	) {
		super(...rest);
		// This module is loaded dynamically, so mark it, see `DialogUtils#showCustomDialog`
		PLATFORM.moduleName('./excel-export-dialog');
	}

	protected attached() {
		super.attached();
		JQueryHelper.focusFirstModifiableInput(this.jQueryElement);
		if (this.validationController) {
			this.validationController.addRenderer(
				this.validationMessageRenderer,
			);
		}
	}

	public activate(
		model: ExcelExportDialogModel,
	): Promise<ExcelExportDialogModel> {
		return super
			.activate(model, ExcelExportDialogModelDefaults)
			.then(() => {
				this.addValidationRules();
				this.validationController.reset();
				return this.model;
			});
	}

	protected deactivate(result: DialogCloseResult | DialogCloseError) {
		super.deactivate(result);
		if (!result.wasCancelled) {
			result.output = this.model;
		}
	}

	protected async canDeactivate(result: DialogCloseResult) {
		let canDeactivate = true;
		if (!result.wasCancelled) {
			await this.validationController.validate().then((r) => {
				canDeactivate = r.valid;
				if (!canDeactivate) {
					Notification.error(
						this.i18n.tr(
							'errors.one-or-more-fields-are-invalid-message',
						),
					);
				}
			});
		}
		return canDeactivate;
	}

	public unbind() {
		if (this.validationController) {
			this.validationController.removeRenderer(
				this.validationMessageRenderer,
			);
		}
	}

	private addValidationRules() {
		this.totalExceedsConfig =
			this.model.recordsTotal &&
			this.model.recordsTotal > this.model.configLimit;
		const exportMax: number = this.totalExceedsConfig
			? this.model.configLimit
			: this.model.recordsTotal;
		ValidationRules.customRule(
			this.EXPORT_RANGE,
			(value) =>
				value === null ||
				value === undefined ||
				value.length === 0 ||
				(parseInt(value, 10) >= 1 && parseInt(value, 10) <= exportMax),
			'validation-errors.number-out-of-range',
			() => ({ min: 1, max: exportMax }),
		);
	}
}
